import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import { User } from "../../../Types/User/UserModel";
import { Auth } from "../../../Types/Auth/AuthModel";
import { UserRole } from "../../../Types/UserRole/UserRoleModel";

interface PasswordUpdatePayload {
  password: string;
  password_confirmation: string;
}

const fetchRolesApi = async (empNo: string): Promise<UserRole[]> => {
  const response = await api.get(`/api/users/${empNo}/roles`);
  return response.data.data as UserRole[];
};

export const login = createAsyncThunk(
  "auth/login",
  async (
    { username, password }: { username: string; password: string },
    thunkAPI,
  ) => {
    try {
      const response = await api.post("/api/auth/login", {
        username,
        password,
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || "Network error");
    }
  },
);

export const updatePasswordThunk = createAsyncThunk(
  "auth/updatePassword",
  async (payload: PasswordUpdatePayload, thunkAPI) => {
    try {
      const response = await api.post("/api/auth/reset-my-password", payload);
      return { message: response.data.message, first_login: 0 };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Password update failed",
      );
    }
  },
);

export const fetchUserAndRoles = createAsyncThunk(
  "auth/fetchUserAndRoles",
  async (_, thunkAPI) => {
    try {
      const userResponse = await api.get("/api/auth/me");
      const user: User = userResponse.data;

      const roles = await fetchRolesApi(user.emp_no);

      return { ...user, roles } as User;
    } catch (error: any) {
      thunkAPI.dispatch(logout());
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch user data",
      );
    }
  },
);

const storedUser = localStorage.getItem("user");

const initialState: Auth = {
  token: localStorage.getItem("access_token") || null,
  user: storedUser ? JSON.parse(storedUser) : null,
  loading: false,
  error: null,
  isAuthenticated: !!localStorage.getItem("access_token"),
  userLoading: false,
  userError: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.userLoading = false;
      state.userError = null;
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        localStorage.setItem("access_token", action.payload.access_token);
      })

      // --- NEW REDUCER CASE: UPDATE PASSWORD FULFILLED ---
      .addCase(updatePasswordThunk.fulfilled, (state, action) => {
        if (state.user) {
          state.user.first_login = false;
          localStorage.setItem("user", JSON.stringify(state.user));
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updatePasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as any;
      })
      // ----------------------------------------------------

      .addCase(fetchUserAndRoles.pending, (state) => {
        state.userLoading = true;
        state.userError = null;
      })
      .addCase(
        fetchUserAndRoles.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.userLoading = false;
          state.user = action.payload;
          state.isAuthenticated = true;
          localStorage.setItem("user", JSON.stringify(action.payload));
        },
      )
      .addCase(fetchUserAndRoles.rejected, (state, action) => {
        state.userLoading = false;
        state.userError = action.payload as any;
        state.user = null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
